import { IConditionalNode } from '../../../helpers/conditional-nodes.helpers';
import {
  WindowsDoorsProducts,
  WindowsDoorsPropertyName,
  Opening,
  FrameMaterial,
  Glazing,
  FireRated,
  WindowsDoorsType,
  WindowsDoorsCategory,
} from './windows-doors.model';

const sideHungFrameMaterialConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FrameMaterial,
    map: {
      [FrameMaterial.WoodAluminum]: WindowsDoorsProducts.SideHungWoodAluminum,
      [FrameMaterial.Wood]: WindowsDoorsProducts.SideHungWood,
      [FrameMaterial.Aluminum]: WindowsDoorsProducts.WindowAluminumTripleGlazed,
    },
  };

const inwardFrameMaterialConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FrameMaterial,
    map: {
      [FrameMaterial.WoodAluminum]: WindowsDoorsProducts.InwardWoodAluminum,
      [FrameMaterial.Wood]: WindowsDoorsProducts.InwardWood,
      [FrameMaterial.Aluminum]: WindowsDoorsProducts.WindowAluminumTripleGlazed,
    },
  };

const fullyReversableFrameMaterialConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FrameMaterial,
    map: {
      [FrameMaterial.WoodAluminum]:
        WindowsDoorsProducts.FullyReversableWoodAluminum,

      [FrameMaterial.Wood]: WindowsDoorsProducts.FullyReversableWood,
      [FrameMaterial.Aluminum]: WindowsDoorsProducts.WindowAluminumTripleGlazed,
    },
  };

const fixedFrameMaterialConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FrameMaterial,
    map: {
      [FrameMaterial.WoodAluminum]: WindowsDoorsProducts.FixedWoodAluminum,
      [FrameMaterial.Wood]: WindowsDoorsProducts.FixedWood,
      [FrameMaterial.Aluminum]: WindowsDoorsProducts.WindowAluminumTripleGlazed,
    },
  };

export const windowConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.Opening,
    map: {
      [Opening.SideHung]: sideHungFrameMaterialConditionalProducts,
      [Opening.Inward]: inwardFrameMaterialConditionalProducts,
      [Opening.FullyReversable]:
        fullyReversableFrameMaterialConditionalProducts,
      [Opening.Fixed]: fixedFrameMaterialConditionalProducts,
    },
  };

const fullGlazingFrameMaterialConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FrameMaterial,
    map: {
      [FrameMaterial.WoodAluminum]:
        WindowsDoorsProducts.DoorExternalFullGlazingWoodAluminum,
      [FrameMaterial.Wood]: WindowsDoorsProducts.DoorExternalFullGlazingWood,
      [FrameMaterial.Aluminum]:
        WindowsDoorsProducts.DoorExternalFullGlazingAluminum,
    },
  };

const semiGlazingFrameMaterialConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FrameMaterial,
    map: {
      [FrameMaterial.WoodAluminum]:
        WindowsDoorsProducts.DoorExternalSemiGlazingWoodAluminum,
      [FrameMaterial.Wood]: WindowsDoorsProducts.DoorExternalSemiGlazingWood,
    },
  };

const noGlazingFrameMaterialConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FrameMaterial,
    map: {
      [FrameMaterial.Wood]: WindowsDoorsProducts.DoorExternalWood,
      [FrameMaterial.SteelStainless]:
        WindowsDoorsProducts.DoorExternalSteelStainless,
      [FrameMaterial.SteelCarbon]: WindowsDoorsProducts.DoorExternalSteelCarbon,
    },
  };

const doorExternalConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.Glazing,
    map: {
      [Glazing.Full]: fullGlazingFrameMaterialConditionalProducts,
      [Glazing.Semi]: semiGlazingFrameMaterialConditionalProducts,
      [Glazing.None]: noGlazingFrameMaterialConditionalProducts,
    },
  };

const fireRatedFrameMaterialConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FrameMaterial,
    map: {
      [FrameMaterial.Wood]: WindowsDoorsProducts.DoorInternalFireRatedWood,
      [FrameMaterial.SteelCarbon]:
        WindowsDoorsProducts.DoorInternalFireRatedSteelCarbon,
      [FrameMaterial.WoodLaminated]:
        WindowsDoorsProducts.DoorInternalFireRatedWoodLaminated,
    },
  };

const nonFireRatedFrameMaterialConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FrameMaterial,
    map: {
      [FrameMaterial.Wood]: WindowsDoorsProducts.DoorInternalWood,
    },
  };

const doorInternalConditionalProducts: IConditionalNode<WindowsDoorsProducts> =
  {
    property: WindowsDoorsPropertyName.FireRated,
    map: {
      [FireRated.True]: [fireRatedFrameMaterialConditionalProducts],
      [FireRated.False]: [nonFireRatedFrameMaterialConditionalProducts],
    },
  };

const glassGoodsConditionalProducts: IConditionalNode<WindowsDoorsProducts> = {
  property: WindowsDoorsPropertyName.WindowsDoorsType,
  map: {
    [WindowsDoorsType.FloatGlass]: WindowsDoorsProducts.GlassGoodsFloatGlass,
    [WindowsDoorsType.FireResistent]:
      WindowsDoorsProducts.GlassGoodsFireResistent,
    [WindowsDoorsType.InsulatingDouble]:
      WindowsDoorsProducts.GlassGoodsInsulatingDouble,
    [WindowsDoorsType.InsulatingTripple]:
      WindowsDoorsProducts.GlassGoodsInsulatingTripple,
    [WindowsDoorsType.SafetyLaminated]:
      WindowsDoorsProducts.GlassGoodsSafetyLaminated,
    [WindowsDoorsType.SafetyToughened]:
      WindowsDoorsProducts.GlassGoodsSafetyToughened,
  },
};

/**
 * Root product id node for window products
 */
export const windowProductIds: IConditionalNode<WindowsDoorsProducts> = {
  property: WindowsDoorsPropertyName.WindowsDoorsCategory,
  map: {
    [WindowsDoorsCategory.Window]: windowConditionalProducts,
    [WindowsDoorsCategory.DoorExternal]: doorExternalConditionalProducts,
    [WindowsDoorsCategory.DoorInternal]: doorInternalConditionalProducts,
    [WindowsDoorsCategory.GlassGoods]: glassGoodsConditionalProducts,
  },
};
